import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PortalDropdown = ({ 
  isOpen, 
  onClose, 
  triggerRef, 
  children, 
  className = '',
  style = {}
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !triggerRef?.current) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
      
      setPosition({
        top: triggerRect.bottom + scrollY,
        left: triggerRect.left + scrollX
      });
    };

    updatePosition();

    // Update position on scroll or resize
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, triggerRef]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    // Add a small delay to prevent immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  const dropdownStyle = {
    position: 'absolute',
    top: position.top,
    left: position.left,
    zIndex: 99999,
    background: '#374151',
    border: '2px solid #6b7280',
    borderRadius: 6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
    minWidth: '150px',
    maxHeight: '200px',
    overflowY: 'auto',
    ...style
  };

  return createPortal(
    <div
      ref={dropdownRef}
      className={className}
      style={dropdownStyle}
    >
      {children}
    </div>,
    document.body
  );
};

export default PortalDropdown;
